"use client";

import { useIntersection } from "@mantine/hooks";
import { gtmSendEvent } from "@wojo/analytics";
import { useAuth } from "@wojo/auth";
import {
    RelatedTours,
    SplitLayout,
    TourAlternateYears,
    TourHero,
    TourNav,
    TourSections,
    TripBuilder,
    TripBuilderButtonBanner,
    useTourPageContext,
} from "@wojo/web-components";
import { PlaceType } from "generated/graphql";
import React, { useEffect } from "react";

export const Screen: React.FC = () => {
    const { ref: tourHeroRef, entry: tourHeroEntry } = useIntersection({
        threshold: 0.5,
    });
    const { ref: tripBuilderRef, entry: tripBuilderEntry } = useIntersection();
    const { pageConfig, tourContent, reviewAggregate } = useTourPageContext();
    const { webSource } = useAuth();
    const market = process.env.NEXT_PUBLIC_MARKET_CODE;
    const tour = tourContent.tour;
    const excursions = tourContent.itineraryItems.flatMap((item) => {
        if (item.__typename === "TourItineraryItemContent") {
            return item.days.flatMap((day) => day.excursionIds);
        }
    });

    useEffect(() => {
        gtmSendEvent({
            event: "view_item",
            ga4Tracking: {
                tourCode: tourContent.tourCode ?? "",
                ga4_object: "TOUR_PAGE",
                ga4_action: "VIEWED",
                webSource: webSource ?? "",
                cityIDs:
                    tour?.places
                        .filter((place) => place.type === PlaceType.City)
                        .map((place) => place._id) ?? [],
            },
            ecommerce: {
                currency: process.env.NEXT_PUBLIC_CURRENCY_CODE || "",
                value: 0,
                items: [
                    {
                        item_id: tourContent.tourCode,
                        item_name: tourContent.name,
                        item_brand: tourContent.businessCode,
                        item_variant: "Tour",
                        quantity: 1,
                        price: 0,
                        market,
                        activityLevel: tour?.activityLevelContent?.name ?? "",
                        classLevel: tour?.classLevelContent?.name || "",
                        hasExtension: !!tourContent.extensionId,
                        hasOptionals: !!excursions.length,
                        rating: reviewAggregate?.avgRating ?? "",
                        tourCode: tourContent.tourCode,
                    },
                ],
            },
        });

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (
        <div data-tour-code={tourContent.tourCode}>
            <TourHero ref={tourHeroRef} />
            <TourNav
                displayTripBuilderAnchor={
                    !tripBuilderEntry?.isIntersecting &&
                    (tripBuilderEntry?.boundingClientRect.bottom || 0) < 0
                }
            />
            <SplitLayout>
                <div>
                    <TourAlternateYears />
                    <TourSections />
                </div>
                <TripBuilder ref={tripBuilderRef} />
            </SplitLayout>
            <RelatedTours />
            <TripBuilderButtonBanner
                isVisible={
                    pageConfig.isTripBuilderStickyButtonVisible &&
                    !tourHeroEntry?.isIntersecting &&
                    !tripBuilderEntry?.isIntersecting
                }
            />
        </div>
    );
};
